import { Field, Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { FiEye, FiEyeOff, FiLogOut } from 'react-icons/fi';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import profileplaceholder from '../../assets/placeholderprofile.png';
import { logout } from '../../features/auth/authSlice';
import { BASE_URL } from '../../utils/constants';
import { getAuthContext } from '../../utils/jwt';
import OtpModal from '../Auth/OtpModal';
import PasswordModal from '../Auth/PasswordModal';
import ProfileView from './ProfileView';

// Typecast icons
const FiLogOutComponent = FiLogOut as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
// const FiEditComponent = FiEdit as unknown as React.ComponentType<{
//   size?: number | string;
//   className?: string;
// }>;
const FiEyeComponent = FiEye as unknown as React.ComponentType<{
  size?: number | string;
}>;
const FiEyeOffComponent = FiEyeOff as unknown as React.ComponentType<{
  size?: number | string;
}>;

interface ProfileData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  companyName: string;
  companyType: string;
  website: string;
  country: string;
  city: string;
  address: string;
  postalCode: string;
  profilePhoto: string;
}

const Profile: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'basic' | 'password'>('basic');
  // const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState<ProfileData>({
    firstName: 'Amy',
    lastName: 'Denham',
    email: 'amy@email.com',
    phone: '96586 62568',
    companyName: 'Triaxa FZCO',
    companyType: 'News',
    website: 'https://www.triaxa.com',
    country: 'USA',
    city: 'Washington, D.C.',
    address: '1723 Constitution Ave NW',
    postalCode: '20006',
    profilePhoto: profileplaceholder,
  });
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timer, setTimer] = useState(0);
  const [resendDisabled, setResendDisabled] = useState(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const savedData = localStorage.getItem('profileData');
    if (savedData) {
      setProfileData(JSON.parse(savedData));
    }
  }, []);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login', { replace: true });
  };

  // const handleUpdate = (updatedData: ProfileData) => {
  //   setProfileData(updatedData);
  //   localStorage.setItem('profileData', JSON.stringify(updatedData));
  //   setIsEditing(false);
  //   toast.success('Profile updated successfully', {
  //     position: 'top-right',
  //     autoClose: 3000,
  //   });
  // };

  const handleForgotPassword = () => {
    setIsOtpModalOpen(true);
    setOtp(['', '', '', '', '', '']);
    setTimer(60);
    setResendDisabled(true);
    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setResendDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (value && index < 5) {
        const nextInput = document.getElementById(`otp-input-${index + 1}`);
        nextInput?.focus();
      }
    }
  };

  const handleOtpKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-input-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handleResend = () => {
    setOtp(['', '', '', '', '', '']);
    setTimer(60);
    setResendDisabled(true);
    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setResendDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleContinue = () => {
    const isOtpValid = otp.every((digit) => digit !== '');
    if (isOtpValid) {
      setIsOtpModalOpen(false);
      setIsPasswordModalOpen(true);
    }
  };

  const handlePasswordModalSubmit = (values: {
    password: string;
    confirmPassword: string;
  }) => {
    if (values.password === values.confirmPassword) {
      toast.success('Password updated successfully', {
        position: 'top-right',
        autoClose: 3000,
      });
      setIsPasswordModalOpen(false);
    }
  };

  const passwordSchema = Yup.object().shape({
    currentPassword: Yup.string().required('Current password is required'),
    newPassword: Yup.string()
      .min(8, '8 character minimum')
      .matches(/[a-z]/, 'One lowercase character')
      .matches(/[A-Z]/, 'One uppercase character')
      .matches(/[\d\W]/, 'One number or special symbol')
      .required('New password is required'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword')], 'Passwords must match')
      .required('Confirm password is required'),
  });

  const handlePasswordUpdate = async (values: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => {
    const { token, tenantId, userId } = getAuthContext();

    if (!token) {
      toast.error('Authentication token not found');
      return;
    }


    try {
      const response = await fetch(`${BASE_URL}/v1/profile/me/password/change`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
          confirmPassword: values.confirmPassword,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        if (response.status === 403) {
          toast.error('Current password is incorrect');
        } else {
          throw new Error(errorText || 'Failed to change password');
        }
        return;
      }

      toast.success('Password updated successfully', {
        position: 'top-right',
        autoClose: 3000,
      });

      setActiveTab('basic');
    } catch (error: any) {
      // console.error('Password change failed:', error);
      toast.error(error.message || 'Failed to update password');
    }
  };

  return (
    <div className='bg-gray-50 min-h-screen p-1'>
      <div className='flex justify-between items-center mb-6'>
        <div>
          <h2 className='text-2xl font-semibold' style={{ color: '#3F5265', fontSize: '24px', fontWeight: 600 }}>
            Profile
          </h2>
          <p style={{ color: '#657584', fontSize: '16px' }} className='text-gray-500 text-sm'>
            Update your name, email, password, and other preferences.
          </p>
        </div>
        <button
          onClick={handleLogout}
          className='flex items-center text-red-600 hover:underline'
          style={{ fontSize: '14px', fontWeight: 500, marginRight: '15px' }}
        >
          <FiLogOutComponent className='mr-1' /> Logout
        </button>
      </div>

      <div className='bg-white rounded-lg shadow-sm border border-gray-200'>
        <div className='border-b flex'>
          <button
            onClick={() => setActiveTab('basic')}
            className={`px-6 py-3 text-sm font-medium ${activeTab === 'basic' ? 'text-sky-600 border-b-2 border-sky-500' : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            Basic Information
          </button>
          <button
            onClick={() => setActiveTab('password')}
            className={`px-6 py-3 text-sm font-medium ${activeTab === 'password' ? 'text-sky-600 border-b-2 border-sky-500' : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            Change Password
          </button>
        </div>

        {activeTab === 'basic' && <ProfileView />}

        {activeTab === 'password' && (
          <div className='p-6'>
            <h3 className='text-lg font-semibold mb-4' style={{ color: '#3F5265' }}>
              Change Password
            </h3>
            <Formik
              initialValues={{
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
                showCurrentPassword: false,
                showNewPassword: false,
                showConfirmPassword: false,
              }}
              validationSchema={passwordSchema}
              onSubmit={handlePasswordUpdate}
            >
              {({ values, errors, touched, setFieldValue, isValid }) => {
                const hasLowercase = /[a-z]/.test(values.newPassword);
                const hasUppercase = /[A-Z]/.test(values.newPassword);
                const hasSpecialOrDigit = /[\d\W]/.test(values.newPassword);
                const minLength = values.newPassword.length >= 8;
                const passwordsMatch = values.newPassword === values.confirmPassword && values.newPassword !== '';
                const allFieldsFilled = values.currentPassword && values.newPassword && values.confirmPassword;

                return (
                  <Form className='space-y-4 max-w-md'>
                    <div>
                      <label className='block text-sm' style={{ color: '#9FA9B2', fontSize: '10px' }}>
                        VERIFY CURRENT PASSWORD<span className='text-red-500'>*</span>
                      </label>
                      <div className='relative'>
                        <input
                          type={values.showCurrentPassword ? 'text' : 'password'}
                          value={values.currentPassword}
                          onChange={(e) => setFieldValue('currentPassword', e.target.value)}
                          className='w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-sky-500'
                        />
                        <button
                          type='button'
                          onClick={() => setFieldValue('showCurrentPassword', !values.showCurrentPassword)}
                          className='absolute inset-y-0 right-3 flex items-center text-black-400'
                        >
                          {values.showCurrentPassword ? <FiEyeComponent size={18} /> : <FiEyeOffComponent size={18} />}
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={handleForgotPassword}
                        className="text-sky-600 text-sm mt-1 block bg-transparent border-none p-0 cursor-pointer hover:underline"
                        style={{ color: '#00A4BD' }}
                      >
                        Forgot password?
                      </button>
                    </div>
                    <div>
                      <label className='block text-sm' style={{ color: '#9FA9B2', fontSize: '10px' }}>
                        NEW PASSWORD<span className='text-red-500'>*</span>
                      </label>
                      <div className='relative'>
                        <Field
                          name='newPassword'
                          type={values.showNewPassword ? 'text' : 'password'}
                          placeholder='Set new password'
                          className='w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400'
                        />
                        <button
                          type='button'
                          onClick={() => setFieldValue('showNewPassword', !values.showNewPassword)}
                          className='absolute inset-y-0 right-3 flex items-center text-black-400'
                        >
                          {values.showNewPassword ? <FiEyeComponent size={18} /> : <FiEyeOffComponent size={18} />}
                        </button>
                      </div>
                      <ul className='text-sm mt-1' style={{ lineHeight: 1.5, paddingLeft: '5px' }}>
                        <li style={{ color: minLength ? 'green' : 'orange', fontSize: '10px' }}>
                          • 8 character minimum
                        </li>
                        <li style={{ color: hasLowercase ? 'green' : 'orange', fontSize: '10px' }}>
                          • One lowercase character
                        </li>
                        <li style={{ color: hasUppercase ? 'green' : 'orange', fontSize: '10px' }}>
                          • One uppercase character
                        </li>
                        <li style={{ color: hasSpecialOrDigit ? 'green' : 'orange', fontSize: '10px' }}>
                          • One number or special symbol
                        </li>
                      </ul>
                    </div>

                    <div>
                      <label className='block text-sm mt-3' style={{ color: '#9FA9B2', fontSize: '10px' }}>
                        CONFIRM NEW PASSWORD<span className='text-red-500'>*</span>
                      </label>
                      <div className='relative'>
                        <Field
                          name='confirmPassword'
                          type={values.showConfirmPassword ? 'text' : 'password'}
                          placeholder='Re-enter new password'
                          className='w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400'
                        />
                        <button
                          type='button'
                          onClick={() => setFieldValue('showConfirmPassword', !values.showConfirmPassword)}
                          className='absolute inset-y-0 right-3 flex items-center text-black-400'
                        >
                          {values.showConfirmPassword ? <FiEyeComponent size={18} /> : <FiEyeOffComponent size={18} />}
                        </button>
                      </div>
                      {values.confirmPassword && (
                        <p className='mt-1' style={{ color: passwordsMatch ? 'green' : 'red', fontSize: '10px', paddingLeft: '10px' }}>
                          {passwordsMatch ? 'Password Matched' : 'Passwords do not match'}
                        </p>
                      )}
                    </div>
                    <button
                      type='submit'
                      disabled={!isValid || !passwordsMatch || !allFieldsFilled}
                      className={`px-4 py-2 rounded ${!isValid || !passwordsMatch || !allFieldsFilled ? 'bg-gray-300 text-white cursor-not-allowed' : 'bg-orange-500 hover:bg-orange-600 text-white'}`}
                    >
                      Update Password
                    </button>
                  </Form>
                );
              }}
            </Formik>
          </div>
        )}
      </div>

      {isOtpModalOpen && (
        <OtpModal
          email={profileData.email}
          otp={otp}
          timer={timer}
          resendDisabled={resendDisabled}
          isOtpValid={otp.every((digit) => digit !== '')}
          onOtpChange={handleOtpChange}
          onOtpKeyDown={handleOtpKeyDown}
          onResend={handleResend}
          onContinue={handleContinue}
          onClose={() => setIsOtpModalOpen(false)}
          isForgotPassword={true}
        />
      )}
      {isPasswordModalOpen && (
        <PasswordModal
          onSubmit={handlePasswordModalSubmit}
          onClose={() => setIsPasswordModalOpen(false)}
          isForgotPassword={true}
        />
      )}
    </div>
  );
};

export default Profile;
